import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * The three tiers in the words core uses: the customer's own people come in by Path A (memberships, Lantern staff among
 * them and audited), outside firms it brings in by Path B (engagements), both at one tenant's data; inside it an LP user
 * sees only the funds they are in. The technical names sit under the plain ones, since the picture serves both tracks.
 */
export const ThreeTiers: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const chip = (x: number, y: number, w: number, text: string) => (
    <g key={text}>
      <rect x={x} y={y} width={w} height="28" rx="14" fill={color.muted} opacity=".14" />
      <text x={x + w / 2} y={y + 19} textAnchor="middle" style={row}>
        {text}
      </text>
    </g>
  );
  const fund = (y: number, name: string, mine: boolean) => (
    <g key={name} opacity={mine ? 1 : 0.35}>
      <rect x="440" y={y} width="200" height="30" rx="4" fill={mine ? color.ok : color.muted} opacity={mine ? 0.2 : 0.14} />
      <text x="452" y={y + 20} style={row}>
        {mine ? `${name} · committed` : name}
      </text>
    </g>
  );

  return (
    <svg viewBox="0 0 940 340" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="The three tiers" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-tiers" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {/* Path A: members */}
      <rect x="20" y="30" width="300" height="120" rx="12" fill="none" stroke={color.accent} strokeWidth="2.5" />
      <text x="40" y="60" style={lbl} fill={color.accent}>
        The customer's own people
      </text>
      <text x="40" y="82" style={sub}>
        Path A · memberships
      </text>
      {chip(40, 100, 86, 'the team')}
      {chip(136, 100, 112, 'Lantern staff')}
      <rect x="254" y="104" width="58" height="20" rx="10" fill="none" stroke={color.warn} strokeWidth="1.5" />
      <text x="283" y="118" textAnchor="middle" fontSize="13" fill={color.warn}>
        audited
      </text>
      {/* Path B: third parties */}
      <rect x="20" y="190" width="300" height="120" rx="12" fill="none" stroke={color.muted} strokeWidth="2.5" />
      <text x="40" y="220" style={lbl} fill={color.fg}>
        Outside firms it brings in
      </text>
      <text x="40" y="242" style={sub}>
        Path B · engagements
      </text>
      {chip(40, 260, 92, 'fund admin')}
      {chip(142, 260, 72, 'auditor')}
      {chip(224, 260, 84, 'custodian')}
      <line x1="322" y1="90" x2="416" y2="120" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-tiers)" />
      <line x1="322" y1="250" x2="416" y2="220" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-tiers)" />
      {/* One tenant */}
      <rect x="420" y="30" width="500" height="280" rx="14" fill="none" stroke={color.muted} strokeWidth="2.5" />
      <text x="440" y="60" style={lbl} fill={color.fg}>
        Tenant
      </text>
      <text x="440" y="82" style={sub}>
        one firm, one set of data
      </text>
      {fund(110, 'Fund I', false)}
      {fund(150, 'Fund II', true)}
      {fund(190, 'Fund III', false)}
      {fund(230, 'Fund IV', false)}
      <rect x="690" y="130" width="210" height="90" rx="10" fill="none" stroke={color.accent} strokeWidth="2" />
      <text x="795" y="162" textAnchor="middle" style={lbl} fill={color.fg}>
        LP user
      </text>
      <text x="795" y="184" textAnchor="middle" style={sub}>
        sees only their funds
      </text>
      <line x1="688" y1="175" x2="646" y2="166" stroke={color.ok} strokeWidth="2" markerEnd="url(#academy-arrow-tiers)" />
    </svg>
  );
};
